import React from 'react';
import { styled } from '@linaria/react';
import { useSpring, animated } from 'react-spring';
import { Check } from 'lucide-react';

import Spacer from '@/components/Spacer';

interface Props {
  answer: string;
  answerIndex: number;
  selectedIndex: number | null;
  disabled: boolean;
  highlightAsCorrect: boolean;
  handleSelect: () => void;
}

function Answer({
  answer,
  answerIndex,
  selectedIndex,
  disabled,
  highlightAsCorrect,
  handleSelect,
}: Props) {
  let position, scale;
  if (typeof selectedIndex !== 'number') {
    position = 0;
    scale = 1;
  } else if (selectedIndex === answerIndex) {
    position = 0;
    scale = 1.045;
  } else if (selectedIndex > answerIndex) {
    position = -5;
    scale = 1;
  } else {
    position = 5;
    scale = 1;
  }

  const isSelected =
    typeof selectedIndex === 'number' &&
    selectedIndex === answerIndex;

  const isHighlighted = isSelected || highlightAsCorrect;

  const style = useSpring({
    transform: `translateY(${position}px) scale(${scale})`,
  });

  const color = highlightAsCorrect
    ? 'var(--color-success'
    : isSelected
      ? 'var(--color-gray-1000)'
      : undefined;

  return (
    <Wrapper
      onClick={handleSelect}
      disabled={disabled}
      style={{
        ...style,
        fontWeight: isHighlighted
          ? 'var(--font-weight-bold)'
          : undefined,
      }}
    >
      {highlightAsCorrect && (
        <CheckmarkWrapper>
          <Check size={24} />
        </CheckmarkWrapper>
      )}
      <Number
        style={{
          '--backdrop-opacity': isHighlighted ? 1 : 0,
          '--backdrop-color': color,
          borderWidth: isHighlighted ? 2 : 1,
        }}
      >
        <NumberText
          style={{
            color: isHighlighted
              ? 'var(--color-background)'
              : undefined,
          }}
        >
          {answerIndex + 1}
        </NumberText>
      </Number>
      <Spacer size={12} />
      <AnswerText style={{ color }}>{answer}</AnswerText>
    </Wrapper>
  );
}

const Wrapper = styled(animated.button)`
  display: flex;
  align-items: center;
  margin-bottom: 4px;
  padding: 8px 16px;
  border-radius: 16px;
  background: var(--color-gray-100);
  will-change: transform;

  &:disabled {
    cursor: not-allowed;
  }
`;

const CheckmarkWrapper = styled.div`
  position: absolute;
  top: 0;
  right: 16px;
  bottom: 0;
  width: 24px;
  height: 24px;
  margin: auto;
  color: var(--color-success);

  svg {
    display: block;
  }
`;

const Number = styled.div`
  position: relative;
  width: 36px;
  height: 36px;
  display: grid;
  place-content: center;
  color: var(--color-gray-700);
  font-weight: var(--font-weight-bold);
  border: 1px solid var(--color-gray-700);
  border-radius: 50%;
  border-color: var(--backdrop-color);

  ${Wrapper}:hover & {
    color: var(--color-gray-1000);
    border-color: var(--backdrop-color);
  }

  &::after {
    content: '';
    opacity: var(--backdrop-opacity);
    position: absolute;
    z-index: 1;
    top: 2px;
    left: 2px;
    right: 2px;
    bottom: 2px;
    background: var(--backdrop-color);
    border-radius: 50%;
  }
`;

const NumberText = styled.span`
  display: inline-block;
  position: relative;
  z-index: 2;
`;

const AnswerText = styled.div`
  flex: 1;
  min-width: 0;
  color: var(--color-text);
`;

export default Answer;
